import { useCallback, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, LitSquadDefinitionListResult, LitSquadDefinitionSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseLitSquadDefinitionsOptions {
  search?: string;
  pageSize?: number;
}

interface UseLitSquadDefinitionsReturn {
  definitions: LitSquadDefinitionSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  page: number;
  totalPages: number;
  goToPage: (page: number) => void;
}

export function useLitSquadDefinitions(
  options: UseLitSquadDefinitionsOptions = {},
): UseLitSquadDefinitionsReturn {
  const limit = options.pageSize || 20;
  const [page, setPage] = useState(1);
  const search = options.search || undefined;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.litSquad.definitionList({ search, page }),
    queryFn: (): Promise<LitSquadDefinitionListResult> =>
      api.listLitSquadDefinitions({
        search,
        limit,
        offset: (page - 1) * limit,
      }),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return {
    definitions: data?.definitions ?? [],
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load definitions' : null,
    refresh: () => {
      refetch();
    },
    page,
    totalPages,
    goToPage,
  };
}
